import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight } from "lucide-react";
import { detectActionRoute } from "@/lib/helpActionRoutes";

interface StepCardProps {
  stepNumber: number;
  content: string;
  articleSlug: string;
}

export function StepCard({ stepNumber, content, articleSlug }: StepCardProps) {
  const storageKey = `help-step-${articleSlug}-${stepNumber}`;
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved === "true") {
      setIsComplete(true);
    }
  }, [storageKey]);

  const handleToggle = (checked: boolean) => {
    setIsComplete(checked);
    localStorage.setItem(storageKey, String(checked));
  };

  const actionRoute = detectActionRoute(content);

  // Don't render if content is empty
  if (!content || content.trim().length === 0) {
    return null;
  }

  return (
    <Card className="p-6 relative overflow-hidden group hover:shadow-md transition-all">
      <div className="absolute top-0 left-0 w-1 h-full bg-primary opacity-50 group-hover:opacity-100 transition-opacity" />
      
      <div className="flex items-start gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold">
            {stepNumber}
          </div>
          <Checkbox
            checked={isComplete}
            onCheckedChange={handleToggle}
            className="mt-1"
          />
        </div>
        
        <div className="flex-1 space-y-3">
          <p className={`text-base ${isComplete ? "line-through text-muted-foreground" : ""}`}>
            {content}
          </p>
          
          {actionRoute && (
            <Link to={actionRoute}>
              <Button variant="outline" size="sm" className="gap-2">
                Try it now
                <ArrowRight className="h-3 w-3" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </Card>
  );
}
